import {
  Edit2,
  Loader2,
  Plus,
  Tag,
  Trash2,
} from "lucide-react";
import React, { useEffect, useState } from "react";

import { useToast } from "../../hooks/use-toast";
import { tagAPI } from "../../services/api";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

interface TagItem {
  id: string;
  name: string;
  color: string;
  employeeCount: number;
  createdAt: string;
}

const PRESET_COLORS = [
  "#3b82f6", // blue
  "#10b981", // green
  "#f59e0b", // amber
  "#ef4444", // red
  "#8b5cf6", // violet
  "#ec4899", // pink
  "#06b6d4", // cyan
  "#f97316", // orange
  "#84cc16", // lime
  "#6366f1", // indigo
];

const TagsPage: React.FC = () => {
  const { toast } = useToast();

  // Tags state
  const [tags, setTags] = useState<TagItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [isTagDialogOpen, setIsTagDialogOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<TagItem | null>(null);
  const [tagName, setTagName] = useState("");
  const [tagColor, setTagColor] = useState("#3b82f6");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingTagId, setDeletingTagId] = useState<string | null>(null);

  const loadTags = async () => {
    try {
      setLoading(true);
      const response = await tagAPI.listTags();
      setTags(response.data);
    } catch (error) {
      console.error("Failed to load tags:", error);
      toast({
        title: "Error",
        description: "Failed to load tags",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrUpdateTag = async () => {
    if (!tagName.trim()) {
      toast({
        title: "Error",
        description: "Tag name is required",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      if (editingTag) {
        await tagAPI.updateTag(editingTag.id, {
          name: tagName.trim(),
          color: tagColor,
        });
        toast({
          title: "Tag Updated",
          description: `Tag "${tagName}" has been updated successfully.`,
        });
      } else {
        await tagAPI.createTag({
          name: tagName.trim(),
          color: tagColor,
        });
        toast({
          title: "Tag Created",
          description: `Tag "${tagName}" has been created successfully.`,
        });
      }
      setIsTagDialogOpen(false);
      setEditingTag(null);
      setTagName("");
      setTagColor("#3b82f6");
      loadTags();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to save tag",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteTag = async (tagId: string) => {
    setDeletingTagId(tagId);
    try {
      await tagAPI.deleteTag(tagId);
      toast({
        title: "Tag Deleted",
        description: "Tag has been deleted successfully.",
      });
      loadTags();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to delete tag",
        variant: "destructive",
      });
    } finally {
      setDeletingTagId(null);
    }
  };

  const openEditDialog = (tag: TagItem) => {
    setEditingTag(tag);
    setTagName(tag.name);
    setTagColor(tag.color);
    setIsTagDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingTag(null);
    setTagName("");
    setTagColor("#3b82f6");
    setIsTagDialogOpen(true);
  };

  useEffect(() => {
    loadTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading tags...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/20 p-6">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
              Tags
            </h1>
            <p className="text-muted-foreground mt-2">
              Create and manage tags to organize your employee invitations
            </p>
          </div>
          <Button onClick={openCreateDialog}>
            <Plus className="h-4 w-4 mr-2" />
            New Tag
          </Button>
        </div>

        {/* Tags List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5" />
              All Tags
            </CardTitle>
            <CardDescription>
              {tags.length} tag{tags.length !== 1 ? "s" : ""} created
            </CardDescription>
          </CardHeader>
          <CardContent>
            {tags.length === 0 ? (
              <div className="text-center py-12">
                <Tag className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">No tags yet</h3>
                <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                  Create tags to organize and categorize your employee invitations.
                  Tags help you filter and manage employees more efficiently.
                </p>
                <Button onClick={openCreateDialog} size="lg">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Tag
                </Button>
              </div>
            ) : (
              <div className="grid gap-3">
                {tags.map((tag) => (
                  <div
                    key={tag.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className="w-6 h-6 rounded-full border-2"
                        style={{ backgroundColor: tag.color, borderColor: tag.color }}
                      />
                      <div>
                        <h4 className="font-semibold text-lg">{tag.name}</h4>
                        <p className="text-sm text-muted-foreground">
                          {tag.employeeCount} employee{tag.employeeCount !== 1 ? "s" : ""} assigned
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        variant="secondary"
                        className="text-xs"
                        style={{
                          backgroundColor: `${tag.color}20`,
                          color: tag.color,
                          borderColor: tag.color,
                        }}
                      >
                        Preview
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(tag)}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteTag(tag.id)}
                        disabled={deletingTagId === tag.id}
                        className="text-destructive hover:text-destructive"
                      >
                        {deletingTagId === tag.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Usage Tips Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">How to use Tags</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10">
                  <span className="text-sm font-medium text-primary">1</span>
                </div>
                <div>
                  <h4 className="font-medium">Create Tags</h4>
                  <p className="text-sm text-muted-foreground">
                    Create tags like "Engineering", "Sales", "Marketing" to categorize employees.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10">
                  <span className="text-sm font-medium text-primary">2</span>
                </div>
                <div>
                  <h4 className="font-medium">Assign Tags</h4>
                  <p className="text-sm text-muted-foreground">
                    When sending invitations, select tags to assign to employees.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10">
                  <span className="text-sm font-medium text-primary">3</span>
                </div>
                <div>
                  <h4 className="font-medium">Filter Employees</h4>
                  <p className="text-sm text-muted-foreground">
                    Use tag filters in the Employees page to quickly find specific groups.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tag Create/Edit Dialog */}
      <Dialog open={isTagDialogOpen} onOpenChange={setIsTagDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingTag ? "Edit Tag" : "Create New Tag"}
            </DialogTitle>
            <DialogDescription>
              {editingTag
                ? "Update the tag name and color."
                : "Create a new tag to organize your employee invitations."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="tagName">Tag Name</Label>
              <Input
                id="tagName"
                placeholder="e.g., Engineering, Sales, Marketing"
                value={tagName}
                onChange={(e) => setTagName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Tag Color</Label>
              <div className="flex flex-wrap gap-2">
                {PRESET_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`w-8 h-8 rounded-full border-2 transition-all ${
                      tagColor === color
                        ? "border-primary scale-110 ring-2 ring-primary/50"
                        : "border-transparent hover:scale-105"
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() => setTagColor(color)}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Label htmlFor="customColor" className="text-sm text-muted-foreground">
                  Custom:
                </Label>
                <Input
                  id="customColor"
                  type="color"
                  value={tagColor}
                  onChange={(e) => setTagColor(e.target.value)}
                  className="w-12 h-8 p-1 cursor-pointer"
                />
                <span className="text-sm text-muted-foreground">{tagColor}</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsTagDialogOpen(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateOrUpdateTag}
              disabled={isSubmitting || !tagName.trim()}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : editingTag ? (
                "Update Tag"
              ) : (
                "Create Tag"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TagsPage;
